<template>
  <div class="product-options">
    <div
      v-if="$slots.default"
      class="product-options__block">
      <slot />
    </div>

    <transition name="transition--opacity">
      <div
        v-if="rangeValues.range"
        class="product-options__block">
        <div
          v-if="showRangeInput"
          class="product-options__block-heading">
          <div class="product-options__block-item">
            <div class="product-options__block-title">
              {{ activeRange.valueSuffix[0] || 'lvl' }}
            </div>

            <ui-input
              :value="rangeValues.range[0]"
              inputmode="number"
              type="number"
              :min="activeRange.borderValues[0]"
              :max="activeRange.borderValues[1]"
              class="product-options__range-amount product-options__block"
              @input="setInputRangeValues(0, Number($event))" />
          </div>

          <div
            v-if="rangeValues.range[1]"
            class="product-options__block-item">
            <div class="product-options__block-title">
              {{ activeRange.valueSuffix[1] || activeRange.valueSuffix[0] || 'lvl' }}
            </div>

            <ui-input
              :value="rangeValues.range[1]"
              inputmode="number"
              type="number"
              :min="activeRange.borderValues[0]"
              :max="activeRange.borderValues[1]"
              class="product-options__range-amount product-options__block"
              @input="setInputRangeValues(1, Number($event))" />
          </div>
        </div>

        <div
          v-else
          class="product-options__block-title">
          {{ rangeValues.range[0] }}
          <template v-if="rangeValues.range[1]">
            - {{ rangeValues.range[1] }}
          </template>
          {{ activeRange.valueSuffix[0] || 'lvl' }}
        </div>

        <ui-range
          :value="rangeValues.range"
          :zones="rangeZones.activeRange"
          :show-scale="true"
          :scale-steps-count="activeRange.scaleStepsCount || 4"
          class="product-options__range"
          @input="setRangeValues('range', $event)"
          @change="emitRangeStatistics('range')" />
      </div>
    </transition>

    <transition name="transition--opacity">
      <div
        v-if="difficulties.length"
        class="product-options__block">
        <div class="product-options__block-title">
          {{ (product.meta && product.meta.difficultyDisplayTitle) || 'Difficulty' }}
        </div>

        <template v-if="isDifficultyRadio">
          <div
            v-for="diff in difficulties"
            :key="`difficulty-${diff.id}`"
            class="product-options__option-grid">
            <ui-radio-check
              :id="diff.id"
              v-model="difficultyId"
              type="radio"
              class="product-options__option"
              display="radio"
              name="difficulty">
              <div class="product-options__radio-grid">
                <span class="product-options__option-title">
                  {{ diff.title }}
                </span>
                <span class="product-options__option-price">
                  {{ diff.displayPrice }}
                </span>
              </div>
            </ui-radio-check>

            <span>
              <info-tooltip
                v-if="diff.additionalInfo"
                :token="`option-info-${difficultyId}-${diff.id}`"
                class="product-options__option-tooltip"
                :text="diff.additionalInfo" />
            </span>
          </div>
        </template>

        <ui-kit-select
          v-else
          v-model="difficultyId"
          value-field="id"
          class="product-options__difficulty-select"
          subtitle-field="displayPrice"
          :items="difficulties"
          @open="revealOptions('difficulties')" />
      </div>
    </transition>

    <transition name="transition--opacity">
      <div
        v-if="allClusterOptions.length"
        ref="optionsContainer">
        <div class="product-options__block">
          <div class="product-options__options-cluster-group">
            <template v-for="(cluster, index) in allClusterOptions">
              <div
                v-if="cluster.options && cluster.options.length"
                :key="`cluster-${cluster.title}`"
                :ref="`cluster-${index}`"
                :class="{
                  'product-options__options-cluster--load-more scrollbar-default':
                    isMobile && !isLoadMoreClickedByClusterName[cluster.title] && cluster.showLoadMore
                }"
                class="product-options__options-cluster">
                <div
                  v-if="cluster.options.length"
                  class="product-options__block-title">
                  {{ cluster.title }}
                  <transition name="transition--opacity">
                    <base-tooltip
                      v-if="isClusterRequired(cluster)"
                      :shown="showRequiredAlert"
                      :token="getClusterToken(cluster)"
                      class="product-options__alert"
                      @hideTooltip="showRequiredAlert = false">
                      <template #default>
                        <span class="product-options__alert-img" />
                      </template>

                      <template #content>
                        Choose at least 1 option
                      </template>
                    </base-tooltip>
                  </transition>
                </div>

                <ul
                  v-if="cluster.displayType === ClusterModel.TYPE.OPTIONS.DISPLAY_TYPE.GROUP"
                  class="product-options__options-scroll-wrapper scrollbar-default">
                  <li
                    v-for="(option, optionIndex) in cluster.options"
                    :key="`option-${option.id}`"
                    :class="{
                      'product-options__option--hidden': optionIndex > cluster.lastOptionIndexBeforeLoadMore + 1
                    }"
                    class="product-options__option-grid">
                    <ui-radio-check
                      v-model="option.isChecked"
                      class="product-options__option"
                      @input="checkGroup(option)">
                      <div class="product-options__radio-grid">
                        <span class="product-options__option-title">
                          {{ option.title }}
                        </span>

                        <span
                          v-if="!option.hideOptionPrice && !cluster.hideOptionsPrice"
                          class="product-options__option-price">
                          {{ option.displayPrice }}
                        </span>
                      </div>
                    </ui-radio-check>

                    <span>
                      <info-tooltip
                        v-if="option.additionalInfo"
                        :token="`option-info-${option.id}`"
                        class="product-options__option-tooltip"
                        :text="option.additionalInfo" />
                    </span>
                  </li>
                </ul>

                <template v-else-if="cluster.displayType === ClusterModel.TYPE.OPTIONS.DISPLAY_TYPE.SELECT">
                  <ui-kit-select
                    class="product-options__difficulty-select"
                    :value="cluster.options[0]"
                    :items="selectOptionsMapper(cluster)"
                    @input="selectClusterOption(cluster, $event)"
                    @open="openClusterSelector(index, cluster.title)" />
                </template>

                <ul
                  v-else-if="cluster.displayType === ClusterModel.TYPE.OPTIONS.DISPLAY_TYPE.RADIO"
                  class="product-options__options-scroll-wrapper scrollbar-default">
                  <li
                    v-for="(option, optionIndex) in cluster.options"
                    :key="`option-${option.id}`"
                    :class="{
                      'product-options__option--hidden': optionIndex > cluster.lastOptionIndexBeforeLoadMore + 1
                    }"
                    class="product-options__option-grid">
                    <ui-radio-check
                      :id="option.id"
                      type="radio"
                      :name="`radio-${cluster.title}`"
                      display="radio"
                      can-uncheck
                      :value="cluster.options.find((option) => option.isChecked)
                        && cluster.options.find((option) => option.isChecked).id"
                      @input="setRadioOptionValue($event, cluster)">
                      <div class="product-options__radio-grid">
                        <span class="product-options__option-title">
                          {{ option.title }}
                        </span>

                        <span
                          v-if="!option.hideOptionPrice && !cluster.hideOptionsPrice"
                          class="product-options__option-price">
                          {{ option.displayPrice }}
                        </span>
                      </div>
                    </ui-radio-check>

                    <span>
                      <info-tooltip
                        v-if="option.additionalInfo"
                        :token="`option-info-${difficultyId}-${option.id}`"
                        :text="option.additionalInfo"
                        class="product-options__option-tooltip" />
                    </span>
                  </li>
                </ul>

                <template
                  v-if="isMobile && !isLoadMoreClickedByClusterName[cluster.title] && cluster.showLoadMore">
                  <div class="product-options__options-gradient" />

                  <div
                    class="product-options__options-button button-round-bordered"
                    @click="revealOptions(cluster.title)">
                    Load more options
                  </div>
                </template>
              </div>
            </template>
          </div>
        </div>
      </div>
    </transition>

    <transition name="transition--opacity">
      <!--      <div v-if="productRank">
        <div class="product-options__block-title">
          Points: {{ rankValue.from * 100 }} - {{ rankValue.to * 100 }}
        </div>

        <shop-range
          key="product-rank"
          :value="rangeValues.rank"
          :border-values="[0, 55]"
          :show-scale="false"
          :show-default-labels="false"
          class="product-options__range"
          @input="setRangeValues('rank', $event)"
          @change="emitRangeStatistics('rank')" />
      </div>-->
    </transition>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import googleTagAnalytics from 'Root/drivers/googleTagAnalytics';
import { transformProductPriceToRange } from 'Root/helpers/product';
import RegionModel from 'Models/RegionModel';
import ClusterModel from 'Models/ClusterModel';
import OptionOperationModel from 'Models/ProductOptionOperationModel';
import UiRange from 'Components/UiRange.vue';
import UiInput from 'Components/ui/Input/UiInput.vue';
import UiKitSelect from 'Components/ui/Select/UiKitSelect.vue';
import BaseTooltip from 'Components/ui/Tooltip/BaseTooltip.vue';
import InfoTooltip from 'Components/ui/Tooltip/InfoTooltip.vue';
import UiRadioCheck from 'Components/ui/RadioCheck/UiRadioCheck.vue';
import { _debounce as debounce } from 'Root/helpers/tools';
import { regExpSelfplay } from 'Root/helpers/regexps';

export default {
  name: 'ProductOptions',

  components: {
    UiInput,
    UiKitSelect,
    InfoTooltip,
    BaseTooltip,
    UiRange,
    UiRadioCheck,
  },

  props: {
    // Объект продукта
    product: {
      type: Object,
      required: true,
    },

    appliedCashback: {
      type: Object,
      default: null,
    },

    runsCount: {
      type: Number,
      default: 1,
    },

    allProductOptions: {
      type: Array,
      default: () => ([]),
    },
  },

  data() {
    return {
      maxRuns: this.product.meta?.quantityMaxRange || 15,
      difficultyId: this.product.meta?.difficulties?.find((d) => !d.disabled)?.id,
      difficultyOptions: [],
      options: [],
      activeRange: null,
      rangeValues: {
        runs: [1],
        rank: [1, 10],
        range: this.activeRange?.value,
      },
      isLoadMoreClickedByClusterName: {},
      showRequiredAlert: false,
      ClusterModel,
    };
  },

  computed: {
    ...mapState(['activeRegion', 'userDevice']),
    ...mapState('products', ['productRaids']),
    ...mapGetters(['currencyValue']),

    rangeZones() {
      const zones = {};

      if (this.activeRange) {
        zones.activeRange = transformProductPriceToRange(this.activeRange);
      }

      return zones;
    },

    isMobile() {
      return this.userDevice.innerWidth < 1024;
    },

    allOptions() {
      return [...this.options, ...this.difficultyOptions];
    },

    allClusterOptions() {
      return [...this.difficultyOptionsClusters, ...this.optionClusters];
    },

    regionShortTitle() {
      return this.activeRegion.shortTitle.toUpperCase();
    },

    difficulty() {
      if (this.difficultyId === undefined) {
        return null;
      }

      return this.difficulties.find((d) => d.id === this.difficultyId);
    },

    difficulties() {
      return this.product.meta?.difficulties?.filter((d) => !d.disabled) || [];
    },

    isDifficultyRadio() {
      return this.product.meta?.difficultyRenderType === 'radio';
    },

    selectedDifficultyPrice() {
      return this.difficulty?.price[this.regionShortTitle] || 0;
    },

    selectedDifficultyOldPrice() {
      return this.difficulty?.price_old?.[this.regionShortTitle] || 0;
    },

    productRank() {
      return this.product.meta?.rank;
    },

    rankValue() {
      return {
        from: this.rangeValues.rank[0],
        to: this.rangeValues.rank[1],
      };
    },

    productRangePrice() {
      if (!this.rangeValues.range || !this.activeRange) {
        return null;
      }

      const [from, to] = this.rangeValues.range;

      const {
        basePrices,
        borderValues,
        exactRanges,
        prices,
        oldPrices,
        zoneSteps,
        step,
      } = this.activeRange;
      const [startBorder, endBorder] = borderValues;
      const basePrice = basePrices?.[this.activeRegion.id];

      let end;
      let startValue;
      let rangePrice = 0;
      let rangeOldPrice = 0;

      if (to) {
        // ренж с двумя значениями
        const exactRangeKey = `${from}-${to}`;
        const exactRangePrice = exactRanges?.[exactRangeKey];
        if (exactRangePrice) {
          return {
            price: exactRangePrice[this.activeRegion.id],
            oldPrice: exactRangePrice[this.activeRegion.id],
          };
        }

        startValue = from;
        end = to;
      } else {
        // ренж с одним значением
        startValue = startBorder;
        end = from;
      }

      let start;

      const metaRangePricesByRegion = prices[this.activeRegion.id];
      const metaRangeOldPricesByRegion = oldPrices?.[this.activeRegion.id] || {};
      const metaRangePricesByRegionKeys = Object.keys(metaRangePricesByRegion);

      // не запускаем расчет шагов если значения ренжа выходят за рамки (например из-за кривых значений по умолчанию)
      if (startValue >= 0 && startValue < endBorder) {
        for (let i = metaRangePricesByRegionKeys.length - 1; i >= 0; i--) {
          const price = metaRangePricesByRegion[metaRangePricesByRegionKeys[i]];
          const oldPrice = metaRangeOldPricesByRegion[metaRangePricesByRegionKeys[i]] || price;
          const pricePoint = +metaRangePricesByRegionKeys[i];

          if (pricePoint > end) {
            // eslint-disable-next-line no-continue
            continue;
          }

          // eslint-disable-next-line no-nested-ternary
          start = startValue > pricePoint
            ? startValue
          /* Для ренжа с одним значением
                   * если указана базовая цена, то первый шаг игнорируется
                   * если базовой цены нет, тогда считаем по старой логике с заменой на актуальную цену
                  */
            : !to && Number(pricePoint) === startBorder && !basePrice
              ? 0
              : pricePoint;

          const currentStep = zoneSteps?.[metaRangePricesByRegionKeys[i]] || step;
          const distance = end - start;

          rangePrice += distance * (price / currentStep);
          rangeOldPrice += distance * (oldPrice / currentStep);

          end -= distance;

          if (startValue === end) {
            break;
          }
        }
      }

      if (basePrices) {
        rangePrice += basePrice;
        rangeOldPrice += basePrice;
      }

      return {
        price: Math.round(rangePrice * 100) / 100,
        oldPrice: Math.round(rangeOldPrice * 100) / 100,
      };
    },

    rankRangePrice() {
      if (!this.productRank) {
        return 0;
      }

      let price = 0;
      for (let i = this.rankValue.from; i < this.rankValue.to; i++) {
        if (i < 21) {
          price += 1.8;
        } else if (i < 35) {
          price += 2.4;
        } else {
          price += 3;
        }
      }
      if (this.activeRegion.id === RegionModel.EU.id) {
        price /= 1.1;
      }
      return Math.round(price * 100) / 100;
    },

    checkedOptions() {
      const all = [];
      const optionsMap = {
        [OptionOperationModel.ADD]: {
          ignoreQuantity: [],
          notIgnoreQuantity: [],
        },
        [OptionOperationModel.MULTIPLY]: {
          ignoreQuantity: [],
          notIgnoreQuantity: [],
        },
      };

      this.allOptions.forEach((o) => {
        if (!o.isChecked) {
          return;
        }

        if (o.ignoreQuantity) {
          optionsMap[o[this.regionShortTitle].operation]?.ignoreQuantity.push(o);
        } else {
          optionsMap[o[this.regionShortTitle].operation]?.notIgnoreQuantity.push(o);
        }

        all.push(o);
      });

      return {
        add: optionsMap[OptionOperationModel.ADD],
        multiply: optionsMap[OptionOperationModel.MULTIPLY],
        all,
      };
    },

    basePrices() {
      let oldPrice = null;
      let newPrice = null;
      let initialPrice = null;
      let initialOldPrice = null;
      let optionsPrice = 0;

      if (this.productRangePrice !== null && this.productRangePrice.price) {
        newPrice = this.productRangePrice.price;
        oldPrice = this.productRangePrice.oldPrice;
      } else if (this.rankRangePrice) {
        optionsPrice = this.rankRangePrice;
        newPrice = +this.product[`cost_${this.regionShortTitle.toLowerCase()}`] + optionsPrice;
        oldPrice = +this.product[`cost_${this.regionShortTitle.toLowerCase()}_old`] + optionsPrice;
      } else {
        newPrice = +this.product[`cost_${this.regionShortTitle.toLowerCase()}`];
        // SKY-5483: если старая базовая цена товара 0,
        // то оставляем ее как 0,
        // если не указана, тогда считаем по старой логике с заменой на актуальную цену
        oldPrice = this.product[`cost_${this.regionShortTitle.toLowerCase()}_old`] === 0
          ? 0
          : +this.product[`cost_${this.regionShortTitle.toLowerCase()}_old`] || newPrice;
      }

      initialPrice = newPrice;
      initialOldPrice = oldPrice;

      const diffPrice = Math.max(this.selectedDifficultyPrice, this.selectedDifficultyOldPrice);

      oldPrice = diffPrice < 0 && !oldPrice
        ? newPrice + diffPrice
        : oldPrice + diffPrice;

      const basePrice = newPrice - optionsPrice;

      return {
        oldPrice,
        newPrice: basePrice + this.selectedDifficultyPrice,
        initialOldPrice,
        initialPrice,
      };
    },

    totalPrices() {
      return {
        newPrice: this.calculateTotalPrice(this.basePrices.newPrice, true).result,
        oldPrice: this.calculateTotalPrice(this.basePrices.oldPrice, false, true).result,
        initialPrice: this.basePrices.initialPrice,
        initialOldPrice: this.basePrices.initialOldPrice,
      };
    },

    isValid() {
      return Object.values(this.showRequired)
        .every((value) => !value.isRequired);
    },

    showRequired() {
      return this.allClusterOptions.reduce((result, cluster) => {
        const clusterKey = cluster.isDifficultyCluster
          ? `${cluster.title}_difficulty`
          : cluster.title;

        // eslint-disable-next-line max-len
        const enabledOptions = cluster.options?.filter((option) => !option[this.activeRegion.shortTitle.toUpperCase()].disabled) || [];

        let isRequired;

        switch (cluster.title) {
          case ClusterModel.TYPE.OPTIONS.DEFAULT_CLUSTER_NAME:
            isRequired = this.product.meta?.isOptionRequired;
            break;
          case ClusterModel.TYPE.OPTIONS.DEFAULT_DIFFICULTY_CLUSTER_NAME:
            isRequired = this.difficulty?.isOptionRequired;
            break;
          default:
            isRequired = cluster.isOptionRequired;
        }

        result[clusterKey] = {
          type: cluster.isDifficultyCluster ? 'difficulty' : 'default',
          isRequired: isRequired && enabledOptions.length && enabledOptions.every((o) => !o.isChecked),
        };

        return result;
      }, {});
    },

    difficultyOptionsByClusterName() {
      const difficultyOptionsByClusterName = {};

      this.difficultyOptions.forEach((option) => {
        const clusterName = option.cluster || 'default';
        if (!difficultyOptionsByClusterName[clusterName]) {
          difficultyOptionsByClusterName[clusterName] = [];
        }
        difficultyOptionsByClusterName[clusterName].push(option);
      });

      return difficultyOptionsByClusterName;
    },

    optionsByClusterName() {
      const optionsByClusterName = {};

      this.options.forEach((option) => {
        const clusterName = option.cluster || 'default';
        if (!optionsByClusterName[clusterName]) {
          optionsByClusterName[clusterName] = [];
        }
        optionsByClusterName[clusterName].push(option);
      });

      return optionsByClusterName;
    },

    difficultyOptionsClusters() {
      const difficultyOptionClusters = [];

      if (this.difficultyOptionsByClusterName.default) {
        const additionalClusterData = this.getAdditionalClusterData(
          this.difficultyOptionsByClusterName.default,
          this.difficulty.optionsCountBeforeLoadMore,
        );
        const defaultCluster = {
          ...ClusterModel.TYPE.OPTIONS.DEFAULT_CLUSTER,
          title: ClusterModel.TYPE.OPTIONS.DEFAULT_DIFFICULTY_CLUSTER_NAME,
          hideOptionsPrice: this.product.meta.hideOptionsPrice,
          options: this.difficultyOptionsByClusterName.default,
          lastOptionIndexBeforeLoadMore: additionalClusterData.lastOptionIndexBeforeLoadMore,
          showLoadMore: additionalClusterData.showLoadMore,
          isDifficultyCluster: true,
        };

        difficultyOptionClusters.push(defaultCluster);
      }

      const clusters = this.product.additional_info?.clusters_difficulty?.[this.difficultyId];

      if (clusters) {
        Object.keys(clusters).forEach((name) => {
          const { optionsCountBeforeLoadMore } = clusters[name];
          const { lastOptionIndexBeforeLoadMore, showLoadMore } = this.getAdditionalClusterData(
            this.difficultyOptionsByClusterName[name],
            optionsCountBeforeLoadMore,
          );
          const modifiedCluster = {
            ...clusters[name],
            title: name,
            options: this.difficultyOptionsByClusterName[name],
            lastOptionIndexBeforeLoadMore,
            showLoadMore,
            isDifficultyCluster: true,
          };

          difficultyOptionClusters.unshift(modifiedCluster);
        });
      }

      return difficultyOptionClusters.sort((a, b) => a.order - b.order);
    },

    optionClusters() {
      const optionClusters = [];

      if (this.optionsByClusterName.default) {
        const additionalClusterData = this.getAdditionalClusterData(
          this.optionsByClusterName.default,
          this.product.meta.optionsCountBeforeLoadMore,
        );
        const defaultCluster = {
          ...ClusterModel.TYPE.OPTIONS.DEFAULT_CLUSTER,
          title: ClusterModel.TYPE.OPTIONS.DEFAULT_CLUSTER_NAME,
          hideOptionsPrice: this.product.meta.hideOptionsPrice,
          options: this.optionsByClusterName.default,
          lastOptionIndexBeforeLoadMore: additionalClusterData.lastOptionIndexBeforeLoadMore,
          showLoadMore: additionalClusterData.showLoadMore,
          isDifficultyCluster: false,
        };

        optionClusters.push(defaultCluster);
      }

      const clusters = this.product.additional_info?.clusters;

      if (clusters) {
        Object.keys(clusters).forEach((name) => {
          const { optionsCountBeforeLoadMore } = clusters[name];
          const { lastOptionIndexBeforeLoadMore, showLoadMore } = this.getAdditionalClusterData(
            this.optionsByClusterName[name],
            optionsCountBeforeLoadMore,
          );
          const modifiedCluster = {
            ...clusters[name],
            title: name,
            options: this.optionsByClusterName[name],
            lastOptionIndexBeforeLoadMore,
            showLoadMore,
            isDifficultyCluster: false,
          };

          optionClusters.unshift(modifiedCluster);
        });
      }

      return optionClusters.sort((a, b) => a.order - b.order);
    },

    showRangeInput() {
      return this.activeRange?.showInput;
    },

    totalExecutionTime() {
      let total = 0;

      total += this.getBaseExecutionTime();
      total += this.getOptionsExecutionTime();
      total += this.getDifficultyExecutionTime();
      total += this.getRangeExecutionTime();
      total = this.applyExecutionTimeQuantity(total);

      return total;
    },

    selectedOptionIds() {
      return this.checkedOptions.all.map((option) => option.id);
    },

    selectedDifficultyId() {
      return this.difficulty ? this.difficulty.id : null;
    },

    selectedRadioOptionsByCluster() {
      const radioOptions = {};
      this.allClusterOptions.forEach((cluster) => {
        if (cluster.displayType === ClusterModel.TYPE.OPTIONS.DISPLAY_TYPE.RADIO) {
          const selectedOption = cluster.options.find((option) => option.isChecked);
          if (selectedOption) {
            radioOptions[cluster.title] = selectedOption.id;
          }
        }
      });

      return radioOptions;
    },

    availableRaidEvents() {
      if (!this.productRaids) {
        return [];
      }

      /**
       * Группирует радио условия по кластерам.
       * @param {Object} raid - Объект рейда.
       * @returns {Object} - Объект с группированными условиями по кластерам.
       */
      const getRadioConditionsByCluster = (raid) => raid.conditions
        .filter((condition) => condition.type === 'product_option')
        .reduce((acc, condition) => {
          const cluster = this.allClusterOptions.find(
            (c) => c.displayType === ClusterModel.TYPE.OPTIONS.DISPLAY_TYPE.RADIO
                                  && c.options.some((opt) => opt.id === condition.id),
          );

          if (cluster) {
            acc[cluster.title] = acc[cluster.title] || [];
            acc[cluster.title].push(condition);
          }

          return acc;
        }, {});

      /**
       * Проверяет, выбраны ли все необходимые радио кластеры.
       * @param {Object} radioConditionsByCluster - Условия, сгруппированные по кластерам.
       * @returns {Boolean} - Результат проверки.
       */
      const hasAllRadioSelections = (radioConditionsByCluster) => Object.keys(radioConditionsByCluster).every(
        (clusterTitle) => this.selectedRadioOptionsByCluster[clusterTitle] !== undefined,
      );

      /**
       * Проверяет соответствие условий рейда (сложность, радио и обычные опции).
       * @param {Object} raid - Объект рейда.
       * @param {Object} radioConditionsByCluster - Условия, сгруппированные по кластерам.
       * @returns {Number} - Количество соответствующих условий, или -1 если не соответствует.
       */
      const getMatchingConditionsCount = (raid, radioConditionsByCluster) => {
        let conditionCount = 0;

        // Check region condition
        const regionConditions = raid.conditions.filter(
          (condition) => condition.type === 'region',
        );

        if (regionConditions.length > 0) {
          const regionMatch = regionConditions.some(
            (condition) => condition.id === this.activeRegion.id,
          );

          if (!regionMatch) {
            return -1;
          }
          conditionCount += 1;
        }

        const radioMatch = Object.entries(radioConditionsByCluster).every(([clusterTitle, conditions]) => {
          const selectedOptionId = this.selectedRadioOptionsByCluster[clusterTitle];
          const hasMatch = conditions.some((condition) => condition.id === selectedOptionId);
          if (hasMatch) {
            conditionCount += 1;
          }
          return hasMatch;
        });

        if (!radioMatch) {
          return -1;
        }

        const difficultyConditions = raid.conditions.filter(
          (condition) => condition.type === 'difficulty' && condition.id === this.selectedDifficultyId,
        );

        if (raid.conditions.some((c) => c.type === 'difficulty') && difficultyConditions.length === 0) {
          return -1;
        }

        const difficultyMatch = difficultyConditions.every((condition) => {
          if (condition.option_id != null) {
            const isMatch = this.selectedOptionIds.includes(condition.option_id);
            if (isMatch) {
              conditionCount += 1;
            }
            return isMatch;
          }

          conditionCount += 1;
          return true;
        });

        if (!difficultyMatch) {
          return -1;
        }

        const regularOptionConditions = raid.conditions.filter(
          (condition) => condition.type === 'product_option'
                        && !this.allClusterOptions.some(
                          (cluster) => cluster.displayType === ClusterModel.TYPE.OPTIONS.DISPLAY_TYPE.RADIO
                                        && cluster.options.some((opt) => opt.id === condition.id),
                        ),
        );

        const regularOptionsMatch = regularOptionConditions.every((condition) => {
          const isMatch = this.selectedOptionIds.includes(condition.id);
          if (isMatch) {
            conditionCount += 1;
          }
          return isMatch;
        });

        if (!regularOptionsMatch) {
          return -1;
        }

        return conditionCount;
      };

      const matchingRaidsWithConditionsCount = this.productRaids.map((raid) => {
        if (!raid.conditions || raid.conditions.length === 0) {
          return { raid, conditionsCount: 0, matches: true };
        }

        const radioConditionsByCluster = getRadioConditionsByCluster(raid);

        if (!hasAllRadioSelections(radioConditionsByCluster)) {
          return { raid, conditionsCount: -1, matches: false };
        }

        const conditionsCount = getMatchingConditionsCount(raid, radioConditionsByCluster);

        if (conditionsCount === -1) {
          return { raid, conditionsCount: -1, matches: false };
        }

        return { raid, conditionsCount, matches: true };
      });

      const validRaids = matchingRaidsWithConditionsCount.filter((item) => item.matches);

      if (validRaids.length === 0) {
        return [];
      }

      const maxConditionsCount = Math.max(...validRaids.map((item) => item.conditionsCount));

      const mostSpecificRaids = validRaids.filter((item) => item.conditionsCount === maxConditionsCount);

      return mostSpecificRaids.map((item) => ({
        raidId: item.raid.raid_id,
        events: item.raid.events,
      }));
    },
  },

  watch: {
    product: {
      handler(newVal) {
        this.options = this.prepareOptions(newVal.options);
        this.difficultyId = newVal.meta?.difficulties?.find((d) => !d.disabled)?.id;
        this.rangeValues.runs = [1];
        this.setRangeData();
      },
      immediate: true,
    },

    difficulty: {
      handler(newVal) {
        this.difficultyOptions = this.prepareOptions(newVal?.options);
        this.setRangeData();
        this.updateDisplayPrices();
        this.$emit('difficulty', newVal);
      },
      immediate: true,
    },

    totalPrices: {
      handler(newVal) {
        this.$emit('price', newVal);
      },
      immediate: true,
      deep: true,
    },

    allOptions: {
      handler(newVal) {
        this.updateDisplayPrices();
        this.$emit('options', newVal);
      },
      immediate: true,
      deep: true,
    },

    allProductOptions: {
      handler(newVal, oldVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
          const clusterCompletion = newVal.find((option) => regExpSelfplay.test(option.title))?.cluster;
          if (!clusterCompletion) {
            return;
          }

          const checkedCompletion = newVal.find((option) => option.cluster === clusterCompletion
                  && option.isChecked);
          if (checkedCompletion) {
            this.allClusterOptions.find((option) => option.title === clusterCompletion)?.options.forEach((option) => {
              option.isChecked = option.title === checkedCompletion.title;
            });
          }
        }
      },
      immediate: true,
      deep: true,
    },

    activeRegion: {
      handler() {
        if (Array.isArray(this.product.options)) {
          this.options = this.prepareOptions(this.product.options);
        }

        if (Array.isArray(this.difficulty?.options)) {
          this.difficultyOptions = this.prepareOptions(this.difficulty.options);
        }

        this.updateDisplayPrices();
      },
    },

    'rangeValues.range': {
      handler() {
        this.updateDisplayPrices();
      },
    },

    'rangeValues.rank': {
      handler() {
        this.updateDisplayPrices();
      },
    },

    allClusterOptions: {
      handler(clusters) {
        // Если опции кластера отображаются в виде селекта, то нужно обязательно выставить значение по умолчанию.
        clusters.forEach((cluster) => {
          const isSelect = cluster.displayType === ClusterModel.TYPE.OPTIONS.DISPLAY_TYPE.SELECT;
          const everyOptionsNotChecked = cluster.options?.every((clusterOption) => !clusterOption.isChecked);

          if (isSelect && everyOptionsNotChecked) {
            this.selectClusterOption(cluster, cluster.options[0]);
          }
        });
      },
      immediate: true,
    },

    totalExecutionTime: {
      handler(newVal) {
        this.$emit('execution-time', newVal);
      },
      immediate: true,
    },

    availableRaidEvents: {
      handler(newEvents) {
        if (newEvents.length > 0) {
          const allRaidEvents = newEvents.map((raid) => raid.events.map((event) => ({
            ...event,
            raidId: raid.raidId,
          }))).flat();

          this.$emit('available-raid-events', allRaidEvents);
        } else {
          this.$emit('available-raid-events', []);
        }
      },
      immediate: true,
      deep: true,
    },
  },

  created() {
    this.setRangeData();

    if (!this.productRaids) {
      this.$store.dispatch('products/fetchProductRaids');
    }
  },

  mounted() {
    if (this.difficultyOptions.length || this.options.length) {
      const allOptions = this.$refs.optionsContainer.querySelectorAll('.product-options__option-grid');

      if (allOptions.length > 15) {
        const observer = new IntersectionObserver(([{ isIntersecting }]) => {
          if (isIntersecting) {
            this.$refs.optionsContainer.classList.remove('product-options__scroll-container');
          } else {
            this.$refs.optionsContainer.classList.add('product-options__scroll-container');
          }
        }, { threshold: 0.95 });

        observer.observe(allOptions[allOptions.length - 1]);
      }
    }
  },

  methods: {
    openClusterSelector(index, clusterName) {
      const clusterElements = document.querySelectorAll('.product-options__options-cluster');
      clusterElements.forEach((el) => {
        if (el === this.$refs[`cluster-${index}`][0]) {
          el.style.zIndex = 1;
          return;
        }
        el.style.zIndex = 0;
      });

      this.revealOptions(clusterName);
    },

    setRangeData() {
      let rangeData;

      if (this.difficulty?.range && !this.difficulty.range.disabled) {
        rangeData = this.difficulty.range;
      } else if (this.product.meta?.range && !this.product.meta.range.disabled) {
        rangeData = this.product.meta.range;
      }

      if (!rangeData) {
        this.activeRange = null;
        this.rangeValues.range = null;
        return;
      }

      rangeData = JSON.parse(JSON.stringify(rangeData));

      this.activeRange = rangeData;

      const idsString = process.env.GAMES_DEFAULT_START_RANGE_X2 || '[]';

      /* SKY-3845, ставим начальное значение в 200к */
      if (JSON.parse(idsString)?.includes(this.product.id)) {
        this.rangeValues.range = [rangeData.value[0] * 2];
      } else {
        this.rangeValues.range = rangeData.value;
      }
    },

    prepareOptions(options) {
      return options?.filter((o) => !o.disabled && !o[this.regionShortTitle].disabled)
        .sort((a, b) => b.priority - a.priority)
        .map((o) => ({
          ...o,
          isChecked: o.enabledByDefault,
          actualPrice: o[this.regionShortTitle].price,
          oldPrice: o[this.regionShortTitle].price_old || 0,
          displayPrice: this.optionDisplayPrice(o),
        })) || [];
    },

    optionDisplayPrice(option) {
      const ratio = (option[this.regionShortTitle].operation === OptionOperationModel.MULTIPLY)
        ? this.calculateTotalPrice(this.basePrices.newPrice).noMultiplyPriceForRun
        : 1;

      const price = (option[this.regionShortTitle].price * ratio).toFixed(2);

      if (+price === 0) {
        return 'Free';
      }

      return `${+price > 0 ? '+' : '-'} ${this.currencyValue(Math.abs(price), this.activeRegion.id)}`;
    },

    updateDisplayPrices() {
      this.allOptions.forEach((o) => {
        o.displayPrice = this.optionDisplayPrice(o);
      });
    },

    checkGroup(option) {
      if (!option.group) {
        return;
      }

      this.allOptions
        .filter((o) => o !== option && o.group === option.group)
        .forEach((o) => {
          o.isChecked = false;
        });
    },

    setRangeValues(field, value) {
      this.rangeValues[field] = value;
    },

    setInputRangeValues: debounce(function (index, value) {
      if (this.rangeValues.range && this.activeRange) {
        if (value === this.rangeValues.range[index]) {
          return;
        }

        let newValue = value;
        let currentStep = this.activeRange.step;
        this.$set(this.rangeValues.range, index, null); /* Нужен, чтобы сработал вотчер на инпуте */

        for (let i = 0; i < this.rangeZones.activeRange.length; i++) {
          const zone = this.rangeZones.activeRange[i];
          if (value >= zone.from && value <= zone.to) {
            currentStep = zone.step;
            break;
          }
        }

        const minValue = index === 0 ? this.activeRange.borderValues[0] : this.rangeValues.range[0] + currentStep;

        if (!index && value >= this.rangeValues.range[1]) { /* index === 0 */
          newValue = this.rangeValues.range[1] - currentStep;
        } else if (index && value <= this.rangeValues.range[0]) { /* index === 1 */
          newValue = Math.max(minValue, this.rangeValues.range[0] + currentStep);
        } else {
          // округляем до ближайшего шага
          newValue = Math.max(minValue, Math.round(value / currentStep) * currentStep);
        }

        this.$nextTick(() => { /* Нужен, чтобы сработал вотчер на инпуте */
          this.$set(this.rangeValues.range, index, newValue);
        });
      }
    }, 500),

    emitRangeStatistics(field) {
      googleTagAnalytics.emitChooseItemOption(field);
    },

    calculateTotalPrice(price, includeCashback = false, shouldCalculateOldPrice = false) {
      const sumOptionsPrice = (options) => options
        .reduce((s, o) => {
          let priceToAdd = o.actualPrice;

          if (shouldCalculateOldPrice) {
            priceToAdd = o.oldPrice === 0 ? 0 : Math.max(o.actualPrice, o.oldPrice);
          }
          return s + priceToAdd;
        }, 0);

      const addIgnoreQuantitySum = sumOptionsPrice(this.checkedOptions.add.ignoreQuantity);
      const addNotIgnoreQuantitySum = sumOptionsPrice(this.checkedOptions.add.notIgnoreQuantity);
      const multiplyIgnoreQuantitySum = sumOptionsPrice(this.checkedOptions.multiply.ignoreQuantity);
      const multiplyNotIgnoreQuantitySum = sumOptionsPrice(this.checkedOptions.multiply.notIgnoreQuantity);

      let result = addIgnoreQuantitySum + addNotIgnoreQuantitySum;
      let quantityPriceForRunSum = addNotIgnoreQuantitySum;
      result += price;

      const noMultiplyPriceForRun = result;

      result += noMultiplyPriceForRun * (multiplyIgnoreQuantitySum + multiplyNotIgnoreQuantitySum);
      quantityPriceForRunSum += noMultiplyPriceForRun * multiplyNotIgnoreQuantitySum;
      result += price * (this.runsCount - 1) + quantityPriceForRunSum * (this.runsCount - 1);
      if (this.appliedCashback?.isApplied && includeCashback) {
        result -= this.appliedCashback?.value;
      }

      result = Math.round(result * 100) / 100;

      return {
        result,
        noMultiplyPriceForRun,
      };

      // Интересный, но не рабочий вариант. TODO: подумать, как упростить/улучшить расчет цены.
      // let noQuantityPrice = price;
      // let quantityPrice = price * this.runsCount;
      //
      // [...this.checkedOptions.add.ignoreQuantity, ...this.checkedOptions.add.notIgnoreQuantity].forEach((option) => {
      //   const count = option.ignoreQuantity ? 1 : this.runsCount;
      //   noQuantityPrice += option.actualPrice;
      //   quantityPrice += option.actualPrice * count;
      // });
      //
      // [...this.checkedOptions.multiply.ignoreQuantity, ...this.checkedOptions.multiply.notIgnoreQuantity]
      //  .forEach((option) => {
      //   const count = option.ignoreQuantity ? 1 : this.runsCount;
      //   quantityPrice += (option.actualPrice * noQuantityPrice) * count;
      // });
      //
      // quantityPrice += this.rankRangePrice;
      // quantityPrice += Math.round(quantityPrice * 100) / 100;
      //
      // return { result: quantityPrice, noMultiplyPriceForRun: noQuantityPrice };
    },

    selectClusterOption(cluster, option) {
      cluster.options.forEach((o) => {
        o.isChecked = false;
      });
      cluster.options.find((o) => o.id === option.id).isChecked = true;
    },

    selectOptionsMapper(cluster) {
      const { options, hideOptionsPrice } = cluster;
      return options.map((option) => ({
        ...option,
        title: `${option.title}${!option[this.activeRegion.shortTitle.toUpperCase()].price || hideOptionsPrice
        || option.hideOptionPrice
          ? ''
          : ` ${option.displayPrice.replace(' ', '')}`}`,
      }));
    },

    revealOptions(name) {
      this.$set(this.isLoadMoreClickedByClusterName, name, true);
    },

    setRadioOptionValue(value, cluster) {
      cluster.options.forEach((option) => {
        if (option.id === +value) {
          option.isChecked = true;
          return;
        }
        option.isChecked = false;
      });
    },

    getAdditionalClusterData(options, optionsCountBeforeLoadMore) {
      const defaultCount = ClusterModel.TYPE.OPTIONS.DEFAULT_CLUSTER.optionsCountBeforeLoadMore;
      if (!optionsCountBeforeLoadMore || +optionsCountBeforeLoadMore < defaultCount) {
        optionsCountBeforeLoadMore = defaultCount;
      }

      const lastOptionIndex = +optionsCountBeforeLoadMore; // берем на 1 опцию больше, чтоб она ушла под градиент
      return {
        lastOptionIndexBeforeLoadMore: lastOptionIndex,
        // надо, чтобы как минимум 2 опции были после, чтобы load more имел смысл
        showLoadMore: options && options.length - 2 > lastOptionIndex,
      };
    },

    isClusterRequired(cluster) {
      const key = cluster.isDifficultyCluster ? `${cluster.title}_difficulty` : cluster.title;
      return this.showRequired[key]?.isRequired;
    },

    getClusterToken(cluster) {
      const title = cluster.title.toLowerCase().replace(' ', '-');
      const suffix = cluster.isDifficultyCluster ? '-difficulty' : '';
      return `choose-at-least-one-${title}${suffix}`;
    },

    getBaseExecutionTime() {
      return this.product.meta.executionTime || 0;
    },

    getOptionsExecutionTime() {
      return this.checkedOptions.all.reduce((sum, option) => sum + (option.executionTime || 0), 0);
    },

    getDifficultyExecutionTime() {
      return this.difficulty?.executionTime || 0;
    },

    getRangeExecutionTime() {
      if (!this.rangeValues.range || !this.activeRange.executionTimes) {
        return 0;
      }

      const [startRange, endRange] = this.rangeValues.range;
      const exactRangeKey = `${startRange}-${endRange}`;

      if (this.activeRange.exactRangesExecutionTimes?.[exactRangeKey]) {
        return this.activeRange.exactRangesExecutionTimes[exactRangeKey];
      }

      const executionTimesKeys = Object.keys(this.activeRange.executionTimes).map(Number);

      if (endRange) {
        return this.calculateRangeExecutionTime(startRange, endRange, executionTimesKeys);
      }
      const startExecutionTime = this.getExecutionTimeForRange(startRange, executionTimesKeys);
      const borderExecutionTime = this.calculateRangeExecutionTime(
        this.activeRange.borderValues[0],
        startRange,
        executionTimesKeys,
      );
      return startExecutionTime + borderExecutionTime;
    },

    calculateRangeExecutionTime(start, end, executionTimesKeys) {
      let total = 0;
      let currentRange = start;

      const getNextRangeBreak = (currentKey) => executionTimesKeys.find((key) => key > currentKey) || end;

      while (currentRange < end) {
        const executionTime = this.getExecutionTimeForRange(currentRange, executionTimesKeys);
        const nextRangeBreak = getNextRangeBreak(currentRange);
        const maxStep = Math.min(nextRangeBreak, end);
        const stepSize = Math.max(this.activeRange.zoneSteps[currentRange] || this.activeRange.step, 1);
        let stepsCount = Math.floor((maxStep - currentRange) / stepSize);

        if (stepsCount === 0) {
          stepsCount = 1;
        }

        total += executionTime * stepsCount;
        currentRange += stepSize * stepsCount;

        if (currentRange >= maxStep && maxStep < end) {
          currentRange = maxStep;
        }
      }

      return total;
    },

    applyExecutionTimeQuantity(total) {
      if (!this.product.meta.executionTimeQuantity || this.runsCount <= 1) {
        return total;
      }

      const { isAdditive, value } = this.product.meta.executionTimeQuantity;

      if (value <= 0) {
        return total;
      }

      if (isAdditive) {
        return total + value * (this.runsCount - 1);
      }
      return total * value * this.runsCount;
    },

    getExecutionTimeForRange(range, executionTimesKeys) {
      const nearestKey = executionTimesKeys.filter((key) => key <= range).sort((a, b) => b - a)[0];

      if (nearestKey !== undefined) {
        return this.activeRange.executionTimes[nearestKey];
      }

      return 0;
    },
  },
};
</script>

<style lang="scss" scoped>
.product-options__block {
  margin-bottom: 20px;
}

.product-options__block-title {
  margin-bottom: 7px;
  font-weight: 500;
  font-size: 13px;
  display: flex;
  align-items: center;
  vertical-align: middle;
}

.product-options__options-cluster-group {
  position: relative;
  z-index: 2;
}

.product-options__options-cluster {
  position: relative;

  &:not(:first-child) {
    margin-top: 10px;
  }
}

.product-options .product-options__difficulty-select {
  --bg-color: #{$color--basic-dark-gray};

  font-weight: 500;
}

.product-options__option-grid {
  display: grid;
  padding: 10px 0;
  grid-template-columns: 1fr 21px;
  grid-gap: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;

  &:not(:last-child) {
    border-bottom: 1px solid $color--additional-menu;
  }
}

.product-options__radio-grid {
  display: grid;
  grid-template-columns: 1fr max-content;
  grid-gap: 10px;
}

.product-options__option-title, .radio-check--active .product-options__option-price {
  color: $color--basic-white;
}

.product-options__option-price {
  color: $color--basic-dark-gray;
}

.product-options .product-options__option-tooltip {
  --width: 240px; // Ширину тултипа менять так

  font-size: 11px;
}

.product-options__scroll-container::after,
.product-options__options-gradient {
  position: absolute;
  bottom: -1px;
  left: 0;
  height: 28%;
  width: 100%;
  background: linear-gradient(180deg, rgba($color--basic-black, 0) 0%, $color--basic-black 60.12%);
  pointer-events: none;
  z-index: 2;
}

.product-options__options-button {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
}

.scale-enter-active, .scale-leave-active {
  transition-property: transform, opacity;
  transition-duration: 0.2s;
  transition-timing-function: ease-out;
}

.scale-enter, .scale-leave-to {
  transform: scale(0, 0);
  opacity: 0;
}

.product-options__alert {
  margin-left: 8px;
  line-height: 0;

  /deep/ .tooltip__body {
    border-radius: 100px;
  }
}

.product-options__alert-img {
  background-color: $color--basic-white;
  -webkit-mask: url(/images/icons/alert.svg) no-repeat center;
  mask: url(/images/icons/alert.svg) no-repeat center;
  -webkit-mask-size: contain;
  mask-size: contain;
  width: 17px;
  height: 16px;
  display: inline-block;
}

.product-options__range {
  font-size: 16px; // для em'ов
  padding: 0 16px;
}

.product-options__block-heading {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  grid-gap: 28px;
}

.product-options__block-item {
  &:not(:first-child) {
    .product-options__block {
      position: relative;

      &::before {
        content: '-';
        position: absolute;
        font-size: 25px;
        font-weight: 500;
        line-height: 40px;
        bottom: 0;
        right: 100%;
        margin: 0 8px;
      }
    }
  }
}

.product-options__range-amount {
  --font-size: 13px;
  --height: 40px;
  --border-radius: 5px;
  --padding: 12px;
  --background-color: #{$color--basic-dark-gray};
}

@media screen and (min-width: 720px) {
  .product-options__block-title {
    margin-bottom: 8px;
    font-size: 15px;
  }

  .product-options__promo-block {
    margin: 12px 0 0;
  }
}

@media screen and #{$screen-less-than-1024} {
  .product-options__options-cluster--load-more {
    position: relative;

    .product-options__option--hidden {
      display: none;
    }
  }
}

@media screen and #{$screen-more-or-1024} {
  .product-options__options-scroll-wrapper {
    max-height: 40vh;
    overflow-y: auto;
  }

  .product-options__scroll-container {
    position: relative;
  }

  .product-options__scroll-container::after {
    content: '';
    height: 40px;
  }
}
</style>
